import { CheckIcon, MapPinIcon } from "lucide-react"
import { observer } from "mobx-react-lite"

import { Button } from "@/components/ui/shadcn/button"
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/shadcn/dialog"
import { Skeleton } from "@/components/ui/shadcn/skeleton"
import appStore from "@/stores/appStore"

type CitySelectorDialogProps = {
  open: boolean
  selectedCity: string
  isLoading: boolean
  hasLoadError: boolean
  onOpenChange: (open: boolean) => void
  onRetry: () => void
}

const CitySelectorDialog = ({ open, selectedCity, isLoading, hasLoadError, onOpenChange, onRetry }: CitySelectorDialogProps) => {
  const cities = appStore.cities.map((city) => city.name).filter(Boolean)

  const selectCity = (city: string) => {
    appStore.setCity(city)
    onOpenChange(false)
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent initialFocus={false} className="sm:max-w-[560px]">
        <DialogHeader className="pr-10">
          <DialogTitle className="text-[26px] font-[650]">Выберите город</DialogTitle>
          <DialogDescription className="max-w-[360px]">
            Покажем наличие и магазины для выбранного города
          </DialogDescription>
        </DialogHeader>

        <div className="max-h-[58svh] overflow-y-auto">
          {isLoading ? (
            <div className="flex flex-wrap gap-2" aria-label="Загрузка городов">
              {Array.from({ length: 8 }, (_, index) => <Skeleton key={index} className="h-11 w-28 rounded-full" />)}
            </div>
          ) : null}

          {hasLoadError ? (
            <div className="flex flex-col items-start gap-4 py-2">
              <p className="text-sm text-muted-foreground">Не удалось загрузить список городов</p>
              <Button type="button" variant="outline" onClick={onRetry}>Повторить</Button>
            </div>
          ) : null}

          {!isLoading && !hasLoadError && cities.length > 0 ? (
            <div className="flex flex-wrap gap-2">
              {cities.map((city) => {
                const isSelected = city === selectedCity

                return (
                  <Button
                    key={city}
                    type="button"
                    variant={isSelected ? "default" : "outline"}
                    size="lg"
                    className="rounded-full text-base"
                    data-motion="static"
                    onClick={() => selectCity(city)}
                  >
                    {isSelected ? <MapPinIcon data-icon="inline-start" /> : null}
                    {city}
                    {isSelected ? <CheckIcon data-icon="inline-end" /> : null}
                  </Button>
                )
              })}
            </div>
          ) : null}

          {!isLoading && !hasLoadError && cities.length === 0 ? (
            <p className="py-2 text-sm text-muted-foreground">Города пока недоступны</p>
          ) : null}
        </div>
      </DialogContent>
    </Dialog>
  )
}

const ObservedCitySelectorDialog = observer(CitySelectorDialog)

export default ObservedCitySelectorDialog
